import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Save, ArrowLeft, Edit2, Plus, Users, Eye, Calendar, Search, FileText, Trash2, ArrowUp } from 'lucide-react';
import { CorePriority, CorePriorityGoal, CreateCorePriorityDto, UpdateCorePriorityDto, EmployeeOption } from '../types/corePriority';
import { useApiClient } from '../hooks/useApiClient';
import { useUserProfile } from '../hooks/useUserProfile';
import { useToast } from '../hooks/useToast';
import { Toast } from '../components/Toast';
import { createDefaultGoals, getGoalValidationError, normalizeGoals } from '../utils/corePriorityGoals';
import { Pagination } from '../components/common/Pagination';

const ITEMS_PER_PAGE = 10;

interface EmployeeCorePriority {
  employeeId: string;
  employeeName: string;
  managerId: string;
  managerName: string;
  year: number;
  corePriority: CorePriority | null;
}

export function ManageCorePriorities() {
  const navigate = useNavigate();
  const { apiCall } = useApiClient();
  const { userProfile, loading: profileLoading, fetchUserProfile } = useUserProfile();
  const { toasts, hideToast, success: showSuccess, error: showError } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [employeeCorePriorities, setEmployeeCorePriorities] = useState<EmployeeCorePriority[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [corePriority, setCorePriority] = useState<CorePriority | null>(null);
  const [goals, setGoals] = useState<CorePriorityGoal[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [showDetailView, setShowDetailView] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Back-to-top scroll listener
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Fetch user profile on mount if not cached
  useEffect(() => {
    if (!userProfile) {
      fetchUserProfile();
    }
  }, [userProfile, fetchUserProfile]);

  // Generate year options (current year and previous 5 years)
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 5; i <= currentYear; i++) {
      years.push(i);
    }
    return years.reverse(); // Most recent first
  };

  const yearOptions = generateYearOptions();

  const filteredEmployeeCorePriorities = useMemo(() => {
    const query = employeeSearch.trim().toLowerCase();
    if (!query) return employeeCorePriorities;
    return employeeCorePriorities.filter((emp) =>
      emp.employeeName.toLowerCase().includes(query)
    );
  }, [employeeCorePriorities, employeeSearch]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [employeeSearch, selectedYear, employeeCorePriorities.length]);

  const totalPages = Math.ceil(filteredEmployeeCorePriorities.length / ITEMS_PER_PAGE);
  const paginatedEmployees = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredEmployeeCorePriorities.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredEmployeeCorePriorities, currentPage]);

  useEffect(() => {
    const loadEmployees = async () => {
      setLoading(true);
      // Use direct reports from user profile
      if (userProfile?.directReports && userProfile.directReports.length > 0) {
        const directReportsOptions: EmployeeOption[] = userProfile.directReports.map(report => ({
          employeeId: report.employeeId,
          employeeName: report.displayName
        }));
        console.log('Direct reports loaded:', directReportsOptions);
        setEmployees(directReportsOptions);
        
        // Immediately set employeeCorePriorities with null priorities to show table
        const initialPriorities: EmployeeCorePriority[] = directReportsOptions.map(emp => ({
          employeeId: emp.employeeId,
          employeeName: emp.employeeName,
          managerId: userProfile.employeeId,
          managerName: userProfile.displayName,
          year: selectedYear,
          corePriority: null
        }));
        setEmployeeCorePriorities(initialPriorities);
      } else {
        console.log('No direct reports found in user profile');
        setEmployees([]);
        setEmployeeCorePriorities([]);
      }
      setLoading(false);
    };

    if (userProfile) {
      loadEmployees();
    }
  }, [userProfile, selectedYear]);

  useEffect(() => {
    if (employees.length > 0 && !showDetailView && userProfile) {
      console.log('Fetching core priorities for employees:', employees);
      fetchAllCorePriorities();
    }
  }, [employees, selectedYear, showDetailView, userProfile]);

  const initializePage = async () => {
    setLoading(true);
    // Use direct reports from user profile
    if (userProfile?.directReports) {
      const directReportsOptions: EmployeeOption[] = userProfile.directReports.map(report => ({
        employeeId: report.employeeId,
        employeeName: report.displayName
      }));
      setEmployees(directReportsOptions);
    }
    setLoading(false);
  };

  const fetchAllCorePriorities = async () => {
    if (!userProfile) {
      console.log('User profile not available');
      return;
    }
    
    console.log('Fetching priorities for', employees.length, 'employees');
    setLoading(true);
    try {
      const priorities: EmployeeCorePriority[] = await Promise.all(
        employees.map(async (emp) => {
          try {
            const response = await apiCall(
              `${process.env.REACT_APP_API_BASE_URL}/api/CorePriorities/employee/${encodeURIComponent(emp.employeeId)}/year/${selectedYear}`
            );

            if (response.ok) {
              const result = await response.json();
              return {
                employeeId: emp.employeeId,
                employeeName: emp.employeeName,
                managerId: userProfile.employeeId,
                managerName: userProfile.displayName,
                year: selectedYear,
                corePriority: result.success && result.data ? result.data : null
              };
            }
          } catch (error) {
            console.error(`Error fetching priorities for ${emp.employeeName}:`, error);
          }
          
          // Always return employee data even if priority fetch fails
          return {
            employeeId: emp.employeeId,
            employeeName: emp.employeeName,
            managerId: userProfile.employeeId,
            managerName: userProfile.displayName,
            year: selectedYear,
            corePriority: null
          };
        })
      );

      console.log('Fetched priorities:', priorities);
      setEmployeeCorePriorities(priorities);
    } catch (error) {
      console.error('Error fetching all core priorities:', error);
      showError('Error loading core priorities');
    } finally {
      setLoading(false);
    }
  };

  const fetchCorePriority = async (employeeId: string, year: number) => {
    try {
      setLoading(true);
      const response = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL}/api/CorePriorities/employee/${employeeId}/year/${year}`
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setCorePriority(result.data);
          setGoals(normalizeGoals(result.data));
          setIsEditing(false);
        } else {
          // No core priorities found for this employee and year
          setCorePriority(null);
          setGoals([]);
          setIsEditing(false);
        }
      } else {
        showError('Failed to fetch core priorities');
      }
    } catch (error) {
      console.error('Error fetching core priorities:', error);
      showError('Error loading core priorities');
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeChange = (employeeId: string) => {
    setSelectedEmployeeId(employeeId);
    if (employeeId) {
      fetchCorePriority(employeeId, selectedYear);
    } else {
      setCorePriority(null);
      setGoals([]);
      setIsEditing(false);
    }
  };

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    if (selectedEmployeeId && showDetailView) {
      fetchCorePriority(selectedEmployeeId, year);
    }
  };

  const handleViewEmployee = (employeeId: string, employeeName: string) => {
    setSelectedEmployeeId(employeeId);
    setShowDetailView(true);
    fetchCorePriority(employeeId, selectedYear);
  };

  const handleBackToTable = () => {
    setShowDetailView(false);
    setSelectedEmployeeId('');
    setCorePriority(null);
    setGoals([]);
    setIsEditing(false);
    fetchAllCorePriorities();
  };

  const getPrioritiesSummary = (items: CorePriorityGoal[]): string =>
    items
      .map((goal, index) => `${index + 1}. ${goal.goal.trim()}: ${goal.description.trim()}`)
      .join('\n');

  const handleGoalFieldChange = <K extends keyof CorePriorityGoal>(index: number, field: K, value: CorePriorityGoal[K]) => {
    setGoals((prev) => prev.map((goal, goalIndex) => (
      goalIndex === index ? { ...goal, [field]: value } : goal
    )));
  };

  const addGoal = () => {
    setGoals((prev) => ([
      ...prev,
      {
        goal: '',
        description: '',
        category: '',
        weightage: undefined
      }
    ]));
  };

  const removeGoal = (index: number) => {
    setGoals((prev) => prev.filter((_, goalIndex) => goalIndex !== index));
  };

  const beginCreate = () => {
    setIsEditing(true);
    setGoals(createDefaultGoals());
  };

  const handleSave = async () => {
    if (!selectedEmployeeId) {
      showError('Please select an employee');
      return;
    }

    const validationError = getGoalValidationError(goals);
    if (validationError) {
      showError(validationError);
      return;
    }

    if (!userProfile) {
      showError('User profile not loaded');
      return;
    }

    setSaving(true);

    try {
      if (corePriority) {
        // Update existing
        const updateDto: UpdateCorePriorityDto = {
          id: corePriority.id,
          priorities: getPrioritiesSummary(goals),
          goals,
          managerId: userProfile.employeeId,
          managerName: userProfile.displayName,
          year: selectedYear
        };

        const response = await apiCall(
          `${process.env.REACT_APP_API_BASE_URL}/api/CorePriorities/${corePriority.id}`,
          {
            method: 'PUT',
            body: JSON.stringify(updateDto)
          }
        );

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setCorePriority(result.data);
            setGoals(normalizeGoals(result.data));
            setIsEditing(false);
            showSuccess('Core priorities updated successfully');
          } else {
            showError(result.message || 'Failed to update core priorities');
          }
        } else {
          showError('Failed to update core priorities');
        }
      } else {
        // Create new
        const selectedEmployee = employees.find(e => e.employeeId === selectedEmployeeId);
        if (!selectedEmployee) {
          showError('Selected employee not found');
          return;
        }

        const createDto: CreateCorePriorityDto = {
          employeeId: selectedEmployeeId,
          employeeName: selectedEmployee.employeeName,
          managerId: userProfile.employeeId,
          managerName: userProfile.displayName,
          priorities: getPrioritiesSummary(goals),
          goals,
          year: selectedYear
        };

        const response = await apiCall(
          `${process.env.REACT_APP_API_BASE_URL}/api/CorePriorities`,
          {
            method: 'POST',
            body: JSON.stringify(createDto)
          }
        );

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setCorePriority(result.data);
            setGoals(normalizeGoals(result.data));
            setIsEditing(false);
            showSuccess('Core priorities created successfully');
          } else {
            showError(result.message || 'Failed to create core priorities');
          }
        } else {
          showError('Failed to create core priorities');
        }
      }
    } catch (error) {
      console.error('Error saving core priorities:', error);
      showError('Error saving core priorities');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (corePriority) {
      setGoals(normalizeGoals(corePriority));
      setIsEditing(false);
    } else {
      setGoals([]);
      setIsEditing(false);
    }
  };

  if ((loading || profileLoading) && !selectedEmployeeId && !showDetailView) {
    return (
      <div className="pt-24 min-h-screen bg-slate-50">
        <main className="max-w-7xl mx-auto px-6 py-8">
          <div className="text-center py-20">
            <Loader2 className="w-12 h-12 text-pink-500 animate-spin mx-auto mb-4" />
            <p className="text-slate-600 text-base font-medium">
              {profileLoading ? 'Loading your profile...' : 'Loading...'}
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="pt-24 min-h-screen bg-slate-50">
      <main className="mx-auto px-10 lg:px-20 xl:px-32 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-pink-500 hover:text-pink-600 transition-colors mb-4 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>

          <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 rounded-2xl p-8 shadow-xl text-white">
            <div className="flex items-center gap-4 mb-2">
              <Users className="w-10 h-10" />
              <h1 className="text-3xl font-bold">Core Priorities</h1>
            </div>
            <p className="text-white/80 ml-14">Set and manage core priorities for your direct reports</p>
          </div>
        </div>

        {/* Show Table View or Detail View */}
        {!showDetailView ? (
          <>
            {/* Filters */}
            <div className="bg-white rounded-2xl p-5 mb-6 shadow-sm border border-slate-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={employeeSearch}
                    onChange={(e) => setEmployeeSearch(e.target.value)}
                    placeholder="Search employee name..."
                    className="w-full bg-slate-50 text-slate-800 border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-300 transition-all placeholder:text-slate-400"
                  />
                </div>
                <div className="flex items-center gap-3 md:justify-end">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <label className="text-sm font-medium text-slate-600">Year:</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    className="bg-slate-50 text-slate-800 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-300 transition-all"
                  >
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Table View */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-200">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-pink-500 animate-spin" />
                </div>
              ) : filteredEmployeeCorePriorities.length === 0 ? (
                <div className="text-center py-12">
                  <div className="p-4 bg-slate-100 rounded-2xl inline-block mb-3">
                    <Users className="w-8 h-8 text-slate-400" />
                  </div>
                  <p className="text-slate-600 font-medium">
                    {employeeSearch.trim() ? 'No employees match your search' : 'No employees found'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-6">Employee Name</th>
                        <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-6">Manager Name</th>
                        <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-6">Year</th>
                        <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-6">Status</th>
                        <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-6">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {paginatedEmployees.map((emp) => (
                        <tr key={`${emp.employeeId}-${emp.year}`} className="hover:bg-slate-50/50 transition-colors">
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center text-white font-semibold text-sm shadow-sm shadow-pink-500/20">
                                {emp.employeeName.charAt(0).toUpperCase()}
                              </div>
                              <span className="text-slate-800 font-medium">{emp.employeeName}</span>
                            </div>
                          </td>
                          <td className="py-4 px-6 text-sm text-slate-600">{emp.managerName}</td>
                          <td className="py-4 px-6 text-center text-sm text-slate-600">{emp.year}</td>
                          <td className="py-4 px-6">
                            <div className="flex justify-center">
                              {emp.corePriority ? (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-600 border border-green-200">
                                  Set
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-600 border border-amber-200">
                                  Not Set
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex justify-center">
                              <button
                                onClick={() => handleViewEmployee(emp.employeeId, emp.employeeName)}
                                className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-lg transition-all text-xs font-medium shadow-sm shadow-pink-500/20"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                {emp.corePriority ? 'View / Edit' : 'Create'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                totalItems={filteredEmployeeCorePriorities.length}
                itemsPerPage={ITEMS_PER_PAGE}
              />
            </div>
          </>
        ) : (
          <>
            {/* Detail View - Employee Selection */}
            <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm border border-slate-200">
              <button
                onClick={handleBackToTable}
                className="flex items-center gap-2 text-pink-500 hover:text-pink-600 transition-colors mb-4 font-medium text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to All Employees
              </button>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Select Employee</label>
                  <select
                    value={selectedEmployeeId}
                    onChange={(e) => handleEmployeeChange(e.target.value)}
                    className="w-full bg-slate-50 text-slate-800 border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-300 transition-all"
                  >
                    <option value="">-- Select an employee --</option>
                {employees.map((emp) => (
                  <option key={emp.employeeId} value={emp.employeeId}>
                    {emp.employeeName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Select Year</label>
              <select
                value={selectedYear}
                onChange={(e) => handleYearChange(Number(e.target.value))}
                className="w-full bg-slate-50 text-slate-800 border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-300 transition-all"
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Core Priorities Editor */}
        {selectedEmployeeId && (
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-800">
                {corePriority ? `Core Priorities — ${selectedYear}` : `Create Core Priorities — ${selectedYear}`}
              </h2>
              {corePriority && !isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-xl font-medium text-sm shadow-sm shadow-pink-500/20 transition-all"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </button>
              )}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-pink-500 animate-spin" />
              </div>
            ) : (
              <>
                {isEditing ? (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-pink-400" />
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                          Goals ({goals.length})
                        </label>
                      </div>
                      <button
                        onClick={addGoal}
                        type="button"
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-pink-50 hover:bg-pink-100 text-pink-600 border border-pink-200 rounded-lg text-xs font-medium transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        Add Goal
                      </button>
                    </div>

                    <div className="space-y-4">
                      {goals.map((goal, index) => (
                        <div key={`goal-${index}`} className="bg-gradient-to-br from-white to-slate-50 rounded-xl p-5 border border-slate-200 space-y-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center text-white font-semibold text-sm shadow-sm shadow-pink-500/20">
                                {index + 1}
                              </span>
                              <p className="text-sm font-medium text-slate-700">Goal {index + 1}</p>
                            </div>
                            <button
                              onClick={() => removeGoal(index)}
                              type="button"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-500 rounded-lg text-xs font-medium transition-colors border border-red-200"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Goal *</label>
                              <input
                                value={goal.goal}
                                onChange={(e) => handleGoalFieldChange(index, 'goal', e.target.value)}
                                className="w-full bg-white text-slate-800 border border-slate-200 rounded-lg px-3 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-300"
                                placeholder="Enter goal title"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Category *</label>
                              <input
                                value={goal.category}
                                onChange={(e) => handleGoalFieldChange(index, 'category', e.target.value)}
                                className="w-full bg-white text-slate-800 border border-slate-200 rounded-lg px-3 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-300"
                                placeholder="Enter category"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Description *</label>
                            <textarea
                              value={goal.description}
                              onChange={(e) => handleGoalFieldChange(index, 'description', e.target.value)}
                              rows={3}
                              className="w-full bg-white text-slate-800 border border-slate-200 rounded-lg px-3 py-2.5 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-300"
                              placeholder="Enter description"
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Weightage *</label>
                              <input
                                type="number"
                                min={1}
                                value={goal.weightage ?? ''}
                                onChange={(e) => handleGoalFieldChange(index, 'weightage', e.target.value ? Number(e.target.value) : undefined)}
                                className="w-full bg-white text-slate-800 border border-slate-200 rounded-lg px-3 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-300"
                                placeholder="Enter weightage"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex items-center justify-between mt-5 pt-4 border-t border-slate-200">
                      <p className="text-xs text-slate-400">
                        Set Goal, Description, Category and Weightage for each item.
                      </p>
                      <div className="flex gap-3">
                        <button
                          onClick={handleCancel}
                          disabled={saving}
                          className="px-5 py-2.5 bg-white text-slate-600 border border-slate-300 rounded-xl font-medium text-sm hover:bg-slate-50 transition-all disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSave}
                          disabled={saving || goals.length === 0}
                          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-xl font-medium text-sm shadow-sm shadow-pink-500/20 transition-all disabled:opacity-50"
                        >
                          {saving ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4" />
                              {corePriority ? 'Update' : 'Create'}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </>
                ) : corePriority ? (
                  <div>
                    <div className="space-y-4 mb-4">
                      {normalizeGoals(corePriority).map((goal, index) => (
                        <div key={`readonly-goal-${index}`} className="bg-gradient-to-br from-white to-slate-50 rounded-xl p-5 border border-slate-200">
                          <div className="flex items-start justify-between gap-4 mb-3">
                            <div className="flex items-center gap-3">
                              <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center text-white font-semibold text-sm shadow-sm shadow-pink-500/20">
                                {index + 1}
                              </span>
                              <div>
                                <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider">Goal {index + 1}</p>
                                <p className="text-base font-medium text-slate-800">{goal.goal}</p>
                              </div>
                            </div>
                            <span className="inline-flex items-center px-3 py-1 rounded-full bg-pink-50 text-pink-600 border border-pink-200 font-semibold text-xs">
                              {goal.weightage ?? '-'}%
                            </span>
                          </div>

                          <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed mb-3 ml-11">{goal.description}</p>

                          <div className="flex flex-wrap gap-2 ml-11">
                            {goal.category && (
                              <span className="inline-flex items-center gap-1.5 text-xs bg-blue-50 text-blue-600 border border-blue-200 rounded-lg px-2.5 py-1 font-medium">
                                {goal.category}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {normalizeGoals(corePriority).length === 0 && (
                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                        <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">{corePriority.priorities}</p>
                      </div>
                    )}

                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="p-4 bg-pink-50 rounded-2xl inline-block mb-3">
                      <Plus className="w-8 h-8 text-pink-400" />
                    </div>
                    <p className="text-slate-500 font-medium mb-4">
                      Create core priorities for this employee for {selectedYear}
                    </p>
                    <button
                      onClick={beginCreate}
                      className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-xl font-medium text-sm shadow-sm shadow-pink-500/20 transition-all mx-auto"
                    >
                      <Plus className="w-5 h-5" />
                      Set Core Priorities
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
          </>
        )}

        {/* Toasts */}
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => hideToast(toast.id)}
          />
        ))}
      </main>

      {/* Back to Top FAB */}
      <button
        onClick={scrollToTop}
        className={`
          fixed bottom-8 right-8 z-50 w-12 h-12 rounded-full
          bg-gradient-to-r from-pink-500 to-purple-600 text-white
          shadow-lg shadow-pink-500/30 hover:shadow-xl hover:shadow-pink-500/40
          flex items-center justify-center
          transition-all duration-300 hover:scale-110
          ${showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}
        `}
        aria-label="Back to top"
      >
        <ArrowUp className="w-5 h-5" />
      </button>
    </div>
  );
}
